/**
 * Octopus - AlertRuleRow (Fase 1000)
 *
 * Linha de uma regra de alerta dentro de uma AlertCategorySection: ícone do
 * tipo + nome + badge de severidade (fonte única severityConfig, D-01
 * Fase 16/17.1 — nunca redefinir cores aqui) + switch de habilitado + resumo
 * de destinatários + ações editar/excluir. Extraído do corpo do card de regra
 * de `EquipmentAlertConfig.js` (L354-421).
 */
import React from 'react';
import { Pencil, Trash2, WifiOff, TrendingUp, CloudSun, Fuel } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { severityConfig } from '@/lib/alertSeverity';

const TYPE_ICONS = {
  offline: WifiOff,
  threshold: TrendingUp,
  weather_route: CloudSun,
  fuel_reserve: Fuel,
};

/**
 * @param {object} props
 * @param {object} props.rule - documento da regra (alert_rules)
 * @param {(rule: object) => void} [props.onToggleEnabled]
 * @param {(rule: object) => void} [props.onEdit]
 * @param {(rule: object) => void} [props.onDelete]
 * @param {string} [props.recipientsSummary] - ex.: "3 telefones" ou omitido
 *   para cair no fallback "herda destinatários da categoria" (AD-07).
 */
const AlertRuleRow = ({ rule, onToggleEnabled, onEdit, onDelete, recipientsSummary }) => {
  const Icon = TYPE_ICONS[rule?.alert_type] || TrendingUp;
  const severity = severityConfig[rule?.severity] || severityConfig.medium;

  return (
    <div
      className={cn(
        'flex items-center gap-3 py-2 px-3 border-b border-border last:border-b-0',
        !rule?.enabled && 'opacity-50',
      )}
    >
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold truncate">{rule?.name}</span>
          <Badge variant="outline" className={cn('text-xs', severity.color, severity.border)}>
            {severity.label}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {recipientsSummary || 'herda destinatários da categoria'}
        </p>
      </div>
      <Switch checked={!!rule?.enabled} onCheckedChange={() => onToggleEnabled?.(rule)} />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => onEdit?.(rule)}
        aria-label="Editar regra"
        title="Editar regra"
      >
        <Pencil className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => onDelete?.(rule)}
        aria-label="Excluir regra"
        title="Excluir regra"
      >
        <Trash2 className="w-4 h-4 text-destructive" />
      </Button>
    </div>
  );
};

export default AlertRuleRow;
